import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { ArcoLogo, BOOKING_URL } from "./shared";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const anchorLinks = [
    { hash: "process", label: "Process" },
    { hash: "why", label: "Why Arco" },
    { hash: "results", label: "Results" },
    { hash: "faq", label: "FAQ" },
  ];

  const handleAnchor = (hash) => (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      e.preventDefault();
      navigate(`/#${hash}`);
    }
    setOpen(false);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0C1732]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" onClick={() => setOpen(false)}>
          <ArcoLogo />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {anchorLinks.map((l) => (
            <a
              key={l.hash}
              href={`/#${l.hash}`}
              onClick={handleAnchor(l.hash)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm text-white/70 hover:text-white transition-colors font-medium"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/appointments"
            data-testid="nav-link-pay-per-call"
            className={`text-sm font-medium transition-colors ${
              location.pathname === "/appointments"
                ? "text-[#FF5C00]"
                : "text-white/70 hover:text-white"
            }`}
          >
            Pay-Per-Call
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="book-call-btn-nav"
            className="hidden md:inline-flex arco-btn-primary items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"
          >
            Book a Call <ArrowUpRight className="w-4 h-4" />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-[#0C1732]/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 space-y-5">
          {anchorLinks.map((l) => (
            <a
              key={l.hash}
              href={`/#${l.hash}`}
              onClick={handleAnchor(l.hash)}
              className="block text-white/80 text-lg font-medium"
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/appointments"
            onClick={() => setOpen(false)}
            className="block text-white/80 text-lg font-medium"
            data-testid="mobile-nav-link-pay-per-call"
          >
            Pay-Per-Call
          </Link>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="book-call-btn-mobile"
            className="inline-flex arco-btn-primary items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold w-full justify-center"
          >
            Book a Call <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      )}
    </header>
  );
}
